import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { DESKTOP_MENUS, flatMenuItems, formatAccelerator } from "@ide/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/overlay";
import { Field, Input, Kbd } from "@/components/ui/primitives";
import { desktop, isMac } from "./bridge";
import { useDesktopUi } from "./ui";

/** Atalhos que o editor trata sozinho (além dos menus). */
const EDITOR_SHORTCUTS: Array<[string, string]> = [
  ["CmdOrCtrl+Enter", "Executar o projeto"],
  ["CmdOrCtrl+Shift+I", "Compartilhar / convidar (no projeto)"],
  ["CmdOrCtrl+F", "Localizar no arquivo"],
  ["CmdOrCtrl+H", "Substituir no arquivo"],
  ["CmdOrCtrl+/", "Comentar linha"],
  ["Alt+Up", "Mover linha para cima"],
  ["Alt+Down", "Mover linha para baixo"],
  ["CmdOrCtrl+D", "Selecionar a próxima ocorrência"],
];

function ShortcutsDialog() {
  const open = useDesktopUi((s) => s.shortcuts);
  const set = useDesktopUi((s) => s.set);
  const rows = [
    ...DESKTOP_MENUS.flatMap((m) =>
      flatMenuItems(m.items).flatMap((i) =>
        "accelerator" in i && i.accelerator ? [[i.accelerator, i.label] as const] : [],
      ),
    ),
    ...EDITOR_SHORTCUTS,
  ].filter(([acc], idx, all) => all.findIndex(([a]) => a === acc) === idx);
  return (
    <Dialog open={open} onOpenChange={(v) => set({ shortcuts: v })}>
      <DialogContent title="Atalhos de teclado" description="Os mesmos no app desktop e no editor da web.">
        <ul className="grid max-h-[60vh] grid-cols-1 gap-x-6 overflow-y-auto pr-1 sm:grid-cols-2">
          {rows.map(([acc, label]) => (
            <li
              key={acc}
              className="flex items-center justify-between gap-3 border-b border-border py-2 text-[13px]"
            >
              <span className="text-fg-muted">{label}</span>
              <Kbd>{formatAccelerator(acc, isMac)}</Kbd>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

function ServerDialog() {
  const open = useDesktopUi((s) => s.server);
  const set = useDesktopUi((s) => s.set);
  return (
    <Dialog open={open} onOpenChange={(v) => set({ server: v })}>
      {open ? <ServerForm onDone={() => set({ server: false })} /> : null}
    </Dialog>
  );
}

function ServerForm({ onDone }: { onDone: () => void }) {
  const [url, setUrl] = useState(window.location.origin);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!desktop) return;
    setBusy(true);
    setError(null);
    const r = await desktop.setServer(url);
    setBusy(false);
    if (r.ok) {
      toast.success("Servidor trocado");
      onDone();
    } else setError(r.error ?? "Não foi possível trocar de servidor.");
  };
  return (
    <DialogContent
      title="Servidor do Kompil"
      description="Para empresas e times com o Kompil no próprio servidor. A sessão é separada por servidor."
    >
      <form onSubmit={(e) => void submit(e)} className="space-y-4">
        <Field
          label="Endereço"
          hint="https://app.sua-empresa.com (http só para localhost)"
          error={error ?? undefined}
        >
          <Input value={url} onChange={(e) => setUrl(e.target.value)} autoFocus spellCheck={false} />
        </Field>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Verificando…" : "Trocar e recarregar"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

export function DesktopDialogs() {
  return (
    <>
      <ShortcutsDialog />
      {desktop ? <ServerDialog /> : null}
    </>
  );
}
